import { graphql } from 'gatsby';
import Link from '@/components/Link/Link';
import { applyPaddingCSS } from '@/utils/generatePadding';
import ResponsiveImage from '@/components/ResponsiveImage/ResponsiveImage';
import Video from '@/components/Video/Video';
import { isRichTextValid, resolveDOMNode } from '../RichTextBlock/utils';
import { ImageElement, RichTextElement } from '@kontent-ai/gatsby-components';

import {
  StyledButtons,
  StyledContainer,
  StyledContent,
  StyledLogo,
  StyledHeadline,
  StyledMediaHero,
  StyledResponsiveBackgroundImage,
  StyledRichText,
  StyledSection,
  StyledSubheadline,
  StyledAnnouncementButton,
  StyledInfoTag,
  StyledLanguagesGridBlockV3,
} from './SecondaryHeroBlock.styles';
import { type BlockHTMLId } from '@/utils/renderBlock';
import NavigationDropdown from '../NavigationDropdown/NavigationDropdown';
import StarRating from '../StarRating/StarRating';
import HeroEmailForm from '../Forms/HeroEmailForm/HeroEmailForm';

type SecondaryHeroLinkedItem = Queries.STAR_RATINGFragment | Queries.HERO_EMAIL_FORMFragment;

const DEFAULT_LOGO_WIDTH = 300;
const DEFAULT_LOGO_HEIGHT = 72;

const SecondaryHeroBlock = ({
  secondaryHeroBlockElements: elements,
  system,
  id,
}: Queries.SECONDARY_HERO_BLOCKFragment & BlockHTMLId) => {
  const {
    logo,
    background_image,
    subheadline,
    headline,
    body,
    buttons,
    visual,
    size,
    layout,
    background_color,
    announcement_button,
    tag,
  } = elements;

  const logoImage = {
    ...logo?.value,
    width: logo?.value?.width ?? DEFAULT_LOGO_WIDTH,
    height: logo?.value?.height ?? DEFAULT_LOGO_HEIGHT,
    description: logo?.value?.description ?? logo?.value?.name,
    image_id: logo?.value?.name ?? '',
    url: logo?.value?.url ?? '',
  };

  const hasBackgroundImage = !!background_image?.value;
  const backgroundColor = background_color?.value ?? 'white';
  const sizeValue = size?.value ?? 'medium';
  const layoutValue = layout?.value ?? 'col_6_6';
  const tagValue = tag?.value;
  const hasButtons = !!buttons?.value?.length;
  const hasBody = !!body?.value;
  const hasSubheadline = !!subheadline?.value;
  const hasVisual = !!visual?.value;
  const hasLogo = !!logo?.value;
  const hasAnnouncementButton = !!announcement_button?.value;
  const hasInfoTag = !!tag?.value;
  const shouldDisplaySubheadline = hasSubheadline && !hasInfoTag;

  const resolveLinkedItems = (item: SecondaryHeroLinkedItem) => {
    switch (item?.__typename) {
      case 'kontent_item_star_rating':
        return <StarRating {...item} />;
      case 'kontent_item_hero_email_form':
        return <HeroEmailForm {...item} />;
      default:
        return null;
    }
  };

  return (
    <StyledSection
      data-codename={system.codename}
      hasVisual={hasVisual}
      css={applyPaddingCSS(elements)}
      id={id}
      backgroundColor={backgroundColor}
    >
      {hasBackgroundImage && <StyledResponsiveBackgroundImage {...background_image.value} />}
      <StyledContainer hasVisual={hasVisual}>
        <StyledContent layout={layoutValue} size={sizeValue} hasVisual={hasVisual}>
          {hasLogo && (
            <StyledLogo hasVisual={hasVisual}>
              <ImageElement objectFit="contain" image={logoImage} />
            </StyledLogo>
          )}
          {hasAnnouncementButton && (
            <StyledAnnouncementButton
              hasVisual={hasVisual}
              backgroundColor={backgroundColor}
              {...announcement_button.value}
            />
          )}
          {hasInfoTag ? <StyledInfoTag {...tagValue!} /> : null}
          {shouldDisplaySubheadline && <StyledSubheadline>{subheadline.value}</StyledSubheadline>}
          <StyledHeadline size={sizeValue}>
            <RichTextElement value={headline.value} />
          </StyledHeadline>
          {hasBody && isRichTextValid(body.value) && (
            <StyledRichText size={sizeValue} hasVisual={hasVisual}>
              <RichTextElement
                linkedItems={body.modular_content as SecondaryHeroLinkedItem[]}
                resolveLinkedItem={resolveLinkedItems}
                resolveDomNode={resolveDOMNode}
                value={body.value}
              />
            </StyledRichText>
          )}
          {hasButtons && (
            <StyledButtons>
              {buttons.value.map((button, index) =>
                button.__typename === 'kontent_item_link' ? (
                  <Link
                    key={button.system.id}
                    variant={index === 0 ? 'primary' : 'secondary'}
                    {...button}
                  />
                ) : (
                  <NavigationDropdown key={button.system.id} {...button} />
                )
              )}
            </StyledButtons>
          )}
        </StyledContent>
        {hasVisual && (
          <StyledMediaHero layout={layoutValue}>
            {visual?.value?.__typename === 'kontent_item_responsive_image' && (
              <ResponsiveImage {...visual.value} fit="contain" />
            )}
            {visual?.value?.__typename === 'kontent_item_video' && <Video {...visual.value} />}
            {visual?.value?.__typename === 'kontent_item_languages_grid_block_v3' && (
              <StyledLanguagesGridBlockV3 backgroundColor={backgroundColor} {...visual.value} />
            )}
          </StyledMediaHero>
        )}
      </StyledContainer>
    </StyledSection>
  );
};

export default SecondaryHeroBlock;

export const fragment = graphql`
  fragment SECONDARY_HERO_BLOCK on kontent_item_secondary_hero_block {
    system {
      codename
      type
      id
    }
    secondaryHeroBlockElements: elements {
      ...COMPONENT_A_B_TEST
      ...COMPONENT_PERSONALIZATION
      ...PADDING
      logo {
        value {
          description
          name
          type
          url
          width
          height
        }
      }
      announcement_button {
        value {
          ...ANNOUNCEMENT_BUTTON
        }
      }
      buttons {
        value {
          __typename
          ... on kontent_item {
            system {
              id
            }
          }
          ...LINK
          ...NAVIGATION_DROPDOWN
        }
      }
      background_image {
        value {
          ...RESPONSIVE_IMAGE
        }
      }
      subheadline {
        value
      }
      tag {
        value {
          ...INFO_TAG
        }
      }
      headline {
        value
      }
      body {
        value
        modular_content {
          ...STAR_RATING
          ...HERO_EMAIL_FORM
        }
      }
      visual {
        value {
          __typename
          ...VIDEO
          ...RESPONSIVE_IMAGE
          ...LANGUAGES_GRID_BLOCK_V3
        }
      }
      size {
        value
      }
      layout {
        value
      }
      background_color {
        value
      }
    }
  }
`;
